(function () {
  'use strict';

  // The supplied production example is 1447 x 2048 px. The circle is centred
  // on the page and the photo area is ~1184 px in diameter. Keeping these
  // dimensions makes the generated print file match the existing workflow.
  const SQUARE_SIZE = 1184;
  const PRINT_WIDTH = 1447;
  const PRINT_HEIGHT = 2048;
  const PRINT_OUTER_DIAMETER = 1240;
  const PRINT_WHITE_DIAMETER = 1226;
  const PRINT_PHOTO_DIAMETER = 1184;
  const EXPORT_MIME = 'image/png';

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function getCanvasPoint(event, canvas) {
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * (canvas.width / rect.width),
      y: (event.clientY - rect.top) * (canvas.height / rect.height)
    };
  }

  function setStatus(widget, message, type) {
    const el = widget.querySelector('[data-lm-status]');
    if (!el) return;
    el.textContent = message || '';
    el.classList.remove('is-error', 'is-ready');
    if (type) el.classList.add('is-' + type);
  }

  function makeCustomizer(widget) {
    const sourceInput = widget.querySelector('[data-lm-source]');
    const originalNameInput = widget.querySelector('[data-lm-original-name]');
    const qualityProperty = widget.querySelector('[data-lm-quality-property]');
    const squareInput = widget.querySelector('[data-lm-square-file]');
    const printInput = widget.querySelector('[data-lm-print-file]');
    const uploadButton = widget.querySelector('[data-lm-upload]');
    const changeButton = widget.querySelector('[data-lm-change]');
    const preview = widget.querySelector('[data-lm-preview]');
    const previewImage = widget.querySelector('[data-lm-preview-image]');
    const qualityEl = widget.querySelector('[data-lm-quality]');
    const modal = document.querySelector('[data-lm-modal]');

    if (!sourceInput || !squareInput || !printInput || !uploadButton || !modal) return;

    const canvas = modal.querySelector('[data-lm-canvas]');
    const ctx = canvas.getContext('2d');
    const closeButton = modal.querySelector('[data-lm-close]');
    const confirmButton = modal.querySelector('[data-lm-confirm]');
    const rotateLeftButton = modal.querySelector('[data-lm-rotate-left]');
    const rotateRightButton = modal.querySelector('[data-lm-rotate-right]');
    const zoomInput = modal.querySelector('[data-lm-zoom]');

    const state = {
      image: null,
      imageUrl: '',
      previewUrl: '',
      rotation: 0,
      zoom: 1,
      offsetX: 0,
      offsetY: 0,
      dragging: false,
      lastX: 0,
      lastY: 0,
      pointers: new Map(),
      pinchStartDistance: 0,
      pinchStartZoom: 1
    };

    function resetPreparedFiles() {
      squareInput.value = '';
      printInput.value = '';
      if (preview) preview.hidden = true;
      if (state.previewUrl) {
        URL.revokeObjectURL(state.previewUrl);
        state.previewUrl = '';
      }
      setStatus(widget, '', '');
    }

    function rotatedDimensions() {
      if (!state.image) return { width: 1, height: 1 };
      const quarterTurns = Math.abs(Math.round(state.rotation / 90)) % 2;
      return quarterTurns === 1
        ? { width: state.image.naturalHeight, height: state.image.naturalWidth }
        : { width: state.image.naturalWidth, height: state.image.naturalHeight };
    }

    function baseScale(width, height) {
      const rotated = rotatedDimensions();
      return Math.max(width / rotated.width, height / rotated.height);
    }

    function constrainOffset(width, height) {
      if (!state.image || !width || !height) return;
      const rotated = rotatedDimensions();
      const scale = baseScale(width, height) * state.zoom;
      const scaledWidth = rotated.width * scale;
      const scaledHeight = rotated.height * scale;
      const maxX = Math.max(0, (scaledWidth - width) / 2);
      const maxY = Math.max(0, (scaledHeight - height) / 2);
      state.offsetX = clamp(state.offsetX, -maxX, maxX);
      state.offsetY = clamp(state.offsetY, -maxY, maxY);
    }

    function drawImage(targetCtx, width, height, offsetScaleX, offsetScaleY) {
      if (!state.image) return;
      const scale = baseScale(width, height) * state.zoom;
      const sx = offsetScaleX == null ? width / canvas.width : offsetScaleX;
      const sy = offsetScaleY == null ? height / canvas.height : offsetScaleY;
      targetCtx.save();
      targetCtx.translate(width / 2 + state.offsetX * sx, height / 2 + state.offsetY * sy);
      targetCtx.rotate((state.rotation * Math.PI) / 180);
      targetCtx.scale(scale, scale);
      targetCtx.drawImage(state.image, -state.image.naturalWidth / 2, -state.image.naturalHeight / 2);
      targetCtx.restore();
    }

    function drawEditor() {
      const width = canvas.width;
      const height = canvas.height;
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#555';
      ctx.fillRect(0, 0, width, height);
      if (!state.image) return;

      constrainOffset(width, height);

      // Dimmed full square outside the circle.
      ctx.globalAlpha = .58;
      drawImage(ctx, width, height);
      ctx.globalAlpha = 1;
      ctx.fillStyle = 'rgba(0,0,0,.50)';
      ctx.fillRect(0, 0, width, height);

      // Bright photo inside the circle.
      ctx.save();
      ctx.beginPath();
      ctx.arc(width / 2, height / 2, Math.min(width, height) * .465, 0, Math.PI * 2);
      ctx.clip();
      drawImage(ctx, width, height);
      ctx.restore();

      // White gap + black guide, matching the production look.
      ctx.save();
      ctx.beginPath();
      ctx.arc(width / 2, height / 2, Math.min(width, height) * .477, 0, Math.PI * 2);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = Math.max(8, width * .018);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(width / 2, height / 2, Math.min(width, height) * .492, 0, Math.PI * 2);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = Math.max(3, width * .006);
      ctx.stroke();
      ctx.restore();
    }

    function resizeCanvas() {
      const body = modal.querySelector('.lovingmirror-modal__body');
      const bounds = body.getBoundingClientRect();
      const sideCss = Math.max(260, Math.min(bounds.width, bounds.height));
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(sideCss * dpr);
      canvas.height = Math.round(sideCss * dpr);
      canvas.style.width = sideCss + 'px';
      canvas.style.height = sideCss + 'px';
      drawEditor();
    }

    function updateQuality() {
      if (!state.image) return;
      const w = state.image.naturalWidth;
      const h = state.image.naturalHeight;
      const shortSide = Math.min(w, h);
      let customer = '';
      let order = '';
      let cls = '';

      if (shortSide >= 1800) {
        customer = 'Отлично качество за печат';
        order = 'ОТЛИЧНО – не е нужен upscale (' + w + '×' + h + ' px)';
        cls = 'is-good';
      } else if (shortSide >= 1200) {
        customer = 'Добро качество за печат';
        order = 'ДОБРО – прегледайте преди печат (' + w + '×' + h + ' px)';
        cls = 'is-medium';
      } else {
        customer = 'Ниска резолюция. Препоръчваме по-качествена снимка.';
        order = 'НИСКО КАЧЕСТВО – НУЖЕН UPSCALE (' + w + '×' + h + ' px)';
        cls = 'is-low';
      }

      if (qualityEl) {
        qualityEl.textContent = customer;
        qualityEl.classList.remove('is-good', 'is-medium', 'is-low');
        qualityEl.classList.add(cls);
      }
      if (qualityProperty) qualityProperty.value = order;
    }

    function openModal() {
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('lovingmirror-modal-open');
      window.setTimeout(resizeCanvas, 30);
    }

    function closeModal() {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('lovingmirror-modal-open');
      state.dragging = false;
      state.pointers.clear();
      canvas.classList.remove('is-dragging');
    }

    function loadFile(file) {
      if (!file || !file.type || file.type.indexOf('image/') !== 0) {
        setStatus(widget, 'Моля, избери изображение.', 'error');
        return;
      }
      if (state.imageUrl) URL.revokeObjectURL(state.imageUrl);
      state.imageUrl = URL.createObjectURL(file);
      state.image = new Image();
      state.image.onload = function () {
        state.rotation = 0;
        state.zoom = 1;
        state.offsetX = 0;
        state.offsetY = 0;
        zoomInput.value = '1';
        updateQuality();
        openModal();
      };
      state.image.onerror = function () {
        setStatus(widget, 'Снимката не може да се зареди. Пробвай с друга.', 'error');
      };
      state.image.src = state.imageUrl;
    }

    function createSquareCanvas() {
      const out = document.createElement('canvas');
      out.width = SQUARE_SIZE;
      out.height = SQUARE_SIZE;
      const outCtx = out.getContext('2d');
      outCtx.fillStyle = '#fff';
      outCtx.fillRect(0, 0, out.width, out.height);
      drawImage(outCtx, out.width, out.height);
      return out;
    }

    function createPrintCanvas(squareCanvas) {
      const out = document.createElement('canvas');
      out.width = PRINT_WIDTH;
      out.height = PRINT_HEIGHT;
      const outCtx = out.getContext('2d');
      const cx = PRINT_WIDTH / 2;
      const cy = PRINT_HEIGHT / 2;

      outCtx.fillStyle = '#fff';
      outCtx.fillRect(0, 0, out.width, out.height);

      // Outer black guide.
      outCtx.beginPath();
      outCtx.arc(cx, cy, PRINT_OUTER_DIAMETER / 2, 0, Math.PI * 2);
      outCtx.fillStyle = '#000';
      outCtx.fill();

      // White spacing ring.
      outCtx.beginPath();
      outCtx.arc(cx, cy, PRINT_WHITE_DIAMETER / 2, 0, Math.PI * 2);
      outCtx.fillStyle = '#fff';
      outCtx.fill();

      // Exact crop from the square file, clipped into the photo circle.
      outCtx.save();
      outCtx.beginPath();
      outCtx.arc(cx, cy, PRINT_PHOTO_DIAMETER / 2, 0, Math.PI * 2);
      outCtx.clip();
      outCtx.drawImage(
        squareCanvas,
        cx - PRINT_PHOTO_DIAMETER / 2,
        cy - PRINT_PHOTO_DIAMETER / 2,
        PRINT_PHOTO_DIAMETER,
        PRINT_PHOTO_DIAMETER
      );
      outCtx.restore();

      return out;
    }

    function createPreviewCanvas(squareCanvas) {
      const size = 900;
      const out = document.createElement('canvas');
      out.width = size;
      out.height = size;
      const outCtx = out.getContext('2d');
      const cx = size / 2;
      const cy = size / 2;
      outCtx.fillStyle = '#fff';
      outCtx.fillRect(0, 0, size, size);
      outCtx.beginPath();
      outCtx.arc(cx, cy, size * .48, 0, Math.PI * 2);
      outCtx.fillStyle = '#000';
      outCtx.fill();
      outCtx.beginPath();
      outCtx.arc(cx, cy, size * .466, 0, Math.PI * 2);
      outCtx.fillStyle = '#fff';
      outCtx.fill();
      outCtx.save();
      outCtx.beginPath();
      outCtx.arc(cx, cy, size * .447, 0, Math.PI * 2);
      outCtx.clip();
      outCtx.drawImage(squareCanvas, size * .053, size * .053, size * .894, size * .894);
      outCtx.restore();
      return out;
    }

    function canvasToBlob(targetCanvas) {
      return new Promise(function (resolve) {
        targetCanvas.toBlob(function (blob) { resolve(blob); }, EXPORT_MIME);
      });
    }

    function putBlobIntoInput(blob, targetInput, fileName) {
      if (!blob || !targetInput || typeof File === 'undefined' || typeof DataTransfer === 'undefined') return false;
      const file = new File([blob], fileName, { type: EXPORT_MIME, lastModified: Date.now() });
      const transfer = new DataTransfer();
      transfer.items.add(file);
      targetInput.files = transfer.files;
      return true;
    }

    async function confirmCrop() {
      if (!state.image) return;
      constrainOffset(canvas.width, canvas.height);
      confirmButton.disabled = true;
      setStatus(widget, 'Подготвям файловете...', '');

      try {
        const squareCanvas = createSquareCanvas();
        const printCanvas = createPrintCanvas(squareCanvas);
        const blobs = await Promise.all([canvasToBlob(squareCanvas), canvasToBlob(printCanvas)]);

        const squareOk = putBlobIntoInput(blobs[0], squareInput, 'lovingmirror-square.png');
        const printOk = putBlobIntoInput(blobs[1], printInput, 'lovingmirror-print-ready.png');
        if (!squareOk || !printOk || !squareInput.files.length || !printInput.files.length) {
          throw new Error('Generated file inputs are not supported by this browser.');
        }

        const previewCanvas = createPreviewCanvas(squareCanvas);
        const previewBlob = await canvasToBlob(previewCanvas);
        if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
        state.previewUrl = URL.createObjectURL(previewBlob);
        previewImage.src = state.previewUrl;
        preview.hidden = false;
        setStatus(widget, 'Снимката е готова.', 'ready');
        closeModal();
      } catch (error) {
        console.error('LovingMirror customizer:', error);
        setStatus(widget, 'Не успях да подготвя снимката. Пробвай с друга снимка или браузър.', 'error');
      } finally {
        confirmButton.disabled = false;
      }
    }

    uploadButton.addEventListener('click', function () { sourceInput.click(); });
    if (changeButton) changeButton.addEventListener('click', function () { sourceInput.click(); });

    sourceInput.addEventListener('change', function () {
      const file = sourceInput.files && sourceInput.files[0];
      if (!file) return;
      resetPreparedFiles();
      if (originalNameInput) originalNameInput.value = file.name || 'Избрана снимка';
      loadFile(file);
    });

    closeButton.addEventListener('click', closeModal);
    confirmButton.addEventListener('click', confirmCrop);
    rotateLeftButton.addEventListener('click', function () {
      state.rotation = (state.rotation - 90) % 360;
      drawEditor();
    });
    rotateRightButton.addEventListener('click', function () {
      state.rotation = (state.rotation + 90) % 360;
      drawEditor();
    });
    zoomInput.addEventListener('input', function () {
      state.zoom = clamp(Number(zoomInput.value) || 1, 1, 4);
      drawEditor();
    });

    function pointerDistance() {
      const points = Array.from(state.pointers.values());
      if (points.length < 2) return 0;
      return Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
    }

    canvas.addEventListener('pointerdown', function (event) {
      if (!state.image) return;
      event.preventDefault();
      const point = getCanvasPoint(event, canvas);
      state.pointers.set(event.pointerId, point);
      if (canvas.setPointerCapture) canvas.setPointerCapture(event.pointerId);
      if (state.pointers.size === 1) {
        state.dragging = true;
        state.lastX = point.x;
        state.lastY = point.y;
      } else if (state.pointers.size === 2) {
        state.dragging = false;
        state.pinchStartDistance = pointerDistance();
        state.pinchStartZoom = state.zoom;
      }
      canvas.classList.add('is-dragging');
    });

    canvas.addEventListener('pointermove', function (event) {
      if (!state.image || !state.pointers.has(event.pointerId)) return;
      event.preventDefault();
      const point = getCanvasPoint(event, canvas);
      state.pointers.set(event.pointerId, point);

      if (state.pointers.size >= 2) {
        const distance = pointerDistance();
        if (state.pinchStartDistance > 0) {
          state.zoom = clamp(state.pinchStartZoom * (distance / state.pinchStartDistance), 1, 4);
          zoomInput.value = String(state.zoom);
          drawEditor();
        }
        return;
      }

      if (!state.dragging) return;
      state.offsetX += point.x - state.lastX;
      state.offsetY += point.y - state.lastY;
      state.lastX = point.x;
      state.lastY = point.y;
      drawEditor();
    });

    function stopPointer(event) {
      if (event && event.pointerId !== undefined) state.pointers.delete(event.pointerId);
      if (state.pointers.size === 1) {
        const remaining = Array.from(state.pointers.values())[0];
        state.dragging = true;
        state.lastX = remaining.x;
        state.lastY = remaining.y;
      } else if (state.pointers.size === 0) {
        state.dragging = false;
        state.pinchStartDistance = 0;
        canvas.classList.remove('is-dragging');
      }
      if (event && event.pointerId !== undefined && canvas.releasePointerCapture) {
        try { canvas.releasePointerCapture(event.pointerId); } catch (e) {}
      }
    }

    canvas.addEventListener('pointerup', stopPointer);
    canvas.addEventListener('pointercancel', stopPointer);
    canvas.addEventListener('pointerleave', stopPointer);
    canvas.addEventListener('wheel', function (event) {
      if (!state.image) return;
      event.preventDefault();
      state.zoom = clamp(state.zoom + (event.deltaY > 0 ? -.06 : .06), 1, 4);
      zoomInput.value = String(state.zoom);
      drawEditor();
    }, { passive: false });

    window.addEventListener('resize', function () { if (!modal.hidden) resizeCanvas(); });
    document.addEventListener('keydown', function (event) { if (!modal.hidden && event.key === 'Escape') closeModal(); });

    const form = widget.closest('form');
    if (form) {
      form.enctype = 'multipart/form-data';
      form.setAttribute('enctype', 'multipart/form-data');

      // Selected Shrine upsells (e.g. the preselected batteries) normally get added
      // by Shrine's AJAX product-form handler. We must bypass that handler so Shopify
      // can receive the real uploaded image files, therefore we add selected upsells
      // ourselves first and only then perform the native multipart form submission.
      function getSelectedUpsellVariantIds() {
        const section = widget.closest('.shopify-section') || document;
        const ids = [];

        section.querySelectorAll('[id^=\"UpsellsBlock-\"] product-info-upsell[data-selected=\"true\"][data-id]').forEach(function (upsell) {
          const id = Number(upsell.dataset.id);
          if (Number.isFinite(id) && id > 0 && ids.indexOf(id) === -1) ids.push(id);
        });

        return ids;
      }

      async function addSelectedUpsells() {
        const ids = getSelectedUpsellVariantIds();
        if (!ids.length) return;

        const response = await fetch(window.Shopify && window.Shopify.routes
          ? window.Shopify.routes.root + 'cart/add.js'
          : '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: ids.map(function (id) { return { id: id, quantity: 1 }; })
          })
        });

        if (!response.ok) {
          let message = 'Не успях да добавя батериите. Опитай отново.';
          try {
            const data = await response.json();
            if (data && (data.description || data.message)) message = data.description || data.message;
          } catch (e) {}
          throw new Error(message);
        }
      }

      // Main ATC: require crop, preserve the selected batteries, then bypass Shrine AJAX
      // so Shopify receives the original, square and print-ready image files.
      form.addEventListener('submit', async function (event) {
        if (!squareInput.files.length || !printInput.files.length) {
          event.preventDefault();
          event.stopImmediatePropagation();
          setStatus(widget, 'Моля, качи и намести снимка преди да добавиш продукта.', 'error');
          widget.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        if (form.dataset.lmSubmitting === 'true') return;
        form.dataset.lmSubmitting = 'true';
        setStatus(widget, 'Добавям продукта и батериите...', '');

        try {
          await addSelectedUpsells();
          setStatus(widget, 'Качвам снимките...', '');
          HTMLFormElement.prototype.submit.call(form);
        } catch (error) {
          console.error('[LovingMirror] Upsell add failed:', error);
          form.dataset.lmSubmitting = 'false';
          setStatus(widget, error && error.message ? error.message : 'Не успях да добавя батериите. Опитай отново.', 'error');
        }
      }, true);

      // Shrine's secondary / sticky ATC buttons can otherwise skip file properties.
      document.addEventListener('click', function (event) {
        const section = widget.closest('.shopify-section') || document;
        const secondary = event.target.closest('[data-skip-cart-button="true"], sticky-atc .main-product-atc');
        if (!secondary || !section.contains(secondary)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!squareInput.files.length || !printInput.files.length) {
          setStatus(widget, 'Моля, качи и намести снимка преди да добавиш продукта.', 'error');
          widget.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        const mainButton = form.querySelector('button[name="add"]');
        if (mainButton) mainButton.click();
      }, true);
    }
  }

  function init() {
    document.querySelectorAll('[data-lm-customizer]').forEach(function (widget) {
      if (widget.dataset.lmInitialized === 'true') return;
      widget.dataset.lmInitialized = 'true';
      makeCustomizer(widget);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
